import React from 'react';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Cross-Program Invocation (CPI)',
    tagline: 'Programs calling programs, without ever exceeding their own privileges.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - a CPI can't grant more privilege than the calling program already has
//   Q1 - every account touched via CPI must trace back to the original transaction's declared list
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 6 of 8" title="Programs can call other programs">
            <Prose>
                A program can invoke another program in the middle of processing an instruction. This is called a
                Cross-Program Invocation, or CPI. A decentralized exchange program, for example, doesn't need to
                reimplement token transfers itself; it can CPI into the Token Program to actually move the tokens,
                reusing already-deployed, already-trusted logic.
            </Prose>
        </StageSection>
    )),

    content('privilege-propagation', () => (
        <StageSection title="A CPI can't manufacture privileges out of nothing">
            <Prose>
                When Program A calls into Program B via CPI, Program A can only pass along the signer and writable
                privileges it itself was already granted for those accounts in the current transaction. There's no
                mechanism for a CPI to grant broader access than the calling program legitimately holds: privilege
                only ever narrows or stays the same as it flows through a chain of calls, never widens.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('depth-and-origin', () => (
        <StageSection title="Every account still traces back to the original transaction">
            <Prose>
                CPIs can nest (Program A calls Program B, which calls Program C) up to a fixed maximum call depth,
                much like an ordinary function-call stack limit. No matter how deep that chain gets, every account
                touched anywhere along the way has to be one of the accounts declared in the original top-level
                transaction from Stage 4. New accounts can't simply appear mid-execution.
            </Prose>
            <KeyPoints items={[
                <>CPI lets programs compose without duplicating trusted logic, one of Solana's more powerful patterns.</>,
                <>One stage left: how the network actually agrees on the order all of this happens in.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
